// Base projectile class used by the projectile pool system

import * as THREE from 'three';

class Projectile {
    constructor() {
        // Create a bullet-like projectile
        const projectileGroup = new THREE.Group();
        
        // Create simple sphere projectile
        const bulletGeometry = new THREE.SphereGeometry(0.2, 8, 8);
        const bulletMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x888888,  // Metallic gray color
            metalness: 0.9,
            roughness: 0.2
        });
        
        // Main bullet body
        this.mesh = new THREE.Mesh(bulletGeometry, bulletMaterial);
        projectileGroup.add(this.mesh);
        
        this.mesh.visible = false; // Initially hidden
        this.isActive = false; // Tracks if the projectile is active
        this.direction = new THREE.Vector3(); // Direction of movement
        
        // Add source weapon tracking
        this.sourceWeapon = null;
        this.damage = 0;
        
        // Store scene reference
        this.scene = window.scene;
    }

    activate(position, direction, speed, sourceWeapon = null) {
        if (!position || !direction || typeof speed !== 'number') {
            console.warn('Invalid parameters in Projectile.activate()');
            return;
        }

        try {
            // Ensure position is valid and copy it
            if (position instanceof THREE.Vector3) {
                this.mesh.position.copy(position);
                console.log("Projectile position set to:", 
                    position.x.toFixed(2), 
                    position.y.toFixed(2), 
                    position.z.toFixed(2)
                );
            } else {
                console.error('Position is not a Vector3:', position);
                return;
            }
            
            // Ensure direction is valid and normalized
            if (direction instanceof THREE.Vector3) {
                this.direction.copy(direction).normalize();
                console.log("Projectile direction set to:", 
                    this.direction.x.toFixed(2), 
                    this.direction.y.toFixed(2), 
                    this.direction.z.toFixed(2)
                );
            } else {
                console.error('Direction is not a Vector3:', direction);
                return;
            }
            
            // Set the speed
            this.speed = speed;
            
            // Store source weapon reference
            this.sourceWeapon = sourceWeapon;
            
            // Get damage from weapon if available
            if (sourceWeapon && typeof sourceWeapon.getDamage === 'function') {
                this.damage = sourceWeapon.getDamage();
                console.log("Using weapon damage:", this.damage);
            } else {
                this.damage = 20; // Default damage
                console.log("Using default damage:", this.damage);
            }
            
            // Make projectile visible
            this.mesh.visible = true;
            this.isActive = true;
            
            // Orient projectile along travel direction
            if (this.direction.lengthSq() > 0) {
                const lookTarget = new THREE.Vector3().addVectors(
                    this.mesh.position, 
                    this.direction
                );
                this.mesh.lookAt(lookTarget);
            }
            
            // Ensure the projectile is in the scene - try multiple options to find the scene
            if (!this.scene) {
                // Try to get the scene from window
                this.scene = window.scene;
                
                // If still not found, try to get from ProjectilePool
                if (!this.scene && window.projectilePool && window.projectilePool.scene) {
                    this.scene = window.projectilePool.scene;
                }
            }
            
            // Add to scene if we have one and it's not already there
            if (this.scene && this.mesh.parent !== this.scene) {
                this.scene.add(this.mesh);
                console.log("Projectile added to scene during activation");
            } else if (!this.scene) {
                console.error("No scene available for projectile!");
            }
        } catch (error) {
            console.error('Error in Projectile.activate():', error);
            this.deactivate();
        }
    }

    update(deltaTime) {
        if (!this.isActive) return; // Skip update if not active
        
        // Move projectile forward based on direction and speed
        this.mesh.position.addScaledVector(this.direction, this.speed * deltaTime);
        
        // Check for terrain collision
        if (window.terrainCollision) {
            const groundHeight = window.terrainCollision.getCollisionHeight(
                this.mesh.position.x, 
                this.mesh.position.z
            );
            
            if (this.mesh.position.y < groundHeight) {
                // Hit terrain, deactivate projectile
                this.deactivate();
                return;
            }
        }
        
        // Safe zombie collision check
        if (window.zombieManager?.zombies) {
            for (const zombie of window.zombieManager.zombies) {
                if (!zombie || !zombie.position) continue;
                
                const distance = this.mesh.position.distanceTo(zombie.position);
                const hitboxSize = 2.5;

                if (distance < hitboxSize) {
                    console.log("Projectile hit zombie at distance:", distance);
                    // Pass the source weapon to track what hit the zombie
                    window.zombieManager.damageZombie(zombie, this.damage, this.sourceWeapon);
                    this.deactivate();
                    break;
                }
            }
        }
        
        // Deactivate if projectile goes too far (out of bounds)
        const distanceFromOrigin = this.mesh.position.length();
        if (distanceFromOrigin > 500) {
            this.deactivate();
        }
    }

    deactivate() {
        // Hide the projectile
        this.mesh.visible = false;
        
        // Mark as inactive
        this.isActive = false;
        
        // Reset position and direction
        this.mesh.position.set(0, 0, 0);
        this.direction.set(0, 0, 0);
        
        // Clear references
        this.sourceWeapon = null;
        this.damage = 0;
    }
}

export default Projectile;
